import { HugeiconsIcon } from "@hugeicons/react";
import { RotateCcwIcon } from "@hugeicons-pro/core-bulk-rounded";

import { useEffect, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useFilters } from "@/lib/filters";
import { useReference } from "@/lib/use-reference";
import { CURRENCIES } from "@/lib/currency";
import { DatePicker } from "@/components/ui/date-picker";
import type { ActivityFilter, RegionFilter, CurrencyCode } from "@/lib/api/types";

interface FilterBarProps {
  /** Hide the activity selector on pages that pin their own activity. */
  showActivity?: boolean;
  /** Hide the channel selector on pages that break down by channel themselves. */
  showChannel?: boolean;
  /** Hide the reporting-currency selector on pages that are inherently per-market local. */
  showCurrency?: boolean;
  /** Hide the market selector on pages that select market their own way (e.g. flag tabs). */
  showRegion?: boolean;
  /** Hide the brand picker (pages where only one brand can have data). */
  showBrand?: boolean;
  /** A page fixed to one activity offers only brands/channels that have rows for it. */
  activity?: "ecom" | "awareness" | "job_ads";
}

type Item = { value: string; label: string };

const ACTIVITY_ITEMS: Item[] = [
  { value: "all", label: "Alle activiteit" },
  { value: "ecom", label: "E-commerce" },
  { value: "non_ecom", label: "Non-ecom" },
  { value: "awareness", label: "Awareness" },
  { value: "job_ads", label: "Job ads" },
];
const REGION_ITEMS: Item[] = [
  { value: "all", label: "Alle markten" },
  { value: "EU", label: "Europe (EU)" },
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
];
const PERIOD_ITEMS: Item[] = [
  { value: "7", label: "7 dagen" },
  { value: "30", label: "30 dagen" },
  { value: "90", label: "90 dagen" },
  { value: "180", label: "180 dagen" },
  { value: "365", label: "1 jaar" },
  { value: "custom", label: "Aangepast…" },
];

/** A compact labelled select. `items` also feeds SelectValue so the trigger
 *  shows the friendly label while the popup is closed. */
function FilterSelect({
  label, value, onChange, items, width, disabled,
}: {
  label: string; value: string; onChange: (v: string) => void;
  items: Item[]; width: string; disabled?: boolean;
}) {
  return (
    <Select items={items} value={value} onValueChange={(v) => v && onChange(v)} disabled={disabled}>
      <SelectTrigger size="sm" className={cn("h-8", width)} aria-label={label}><SelectValue /></SelectTrigger>
      <SelectContent>
        {items.map((it) => (
          <SelectItem key={it.value} value={it.value}>{it.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Global filter toolbar. Designed to drop into the PageHeader `tabs` slot so it
 * reads as the bottom strip of the page header — matching prado.
 */
export function GlobalFilterBar({ showActivity = true, showChannel = true, showCurrency = true, showRegion = true, showBrand = true, activity }: FilterBarProps) {
  const f = useFilters();
  const { brands, platforms, availability } = useReference();
  const lastCompleteDay = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);
  const dirty = f.activity !== "all" || f.region !== "all" || f.brand !== "all" || f.channel !== "all" || f.currency !== "EUR" || f.days !== 30 || !!f.range;
  // A single-market selection fixes the display currency to that market's local currency.
  const currencyLocked = f.region !== "all";

  // Which brands / channels the selected market actually uses (from the configured
  // account_map). Scoped by region only — a brand must not disappear from the picker
  // just because the chosen activity has no rows yet; an empty result for a real
  // selection is honest, a hidden brand is not.
  // A page pinned to one activity (Jobs, Awareness) narrows further: Atelier
  // Luxus has no job ads, so it has no business in that page's brand picker.
  // A page without the market picker (Markten) shows every market, so a market
  // chosen on another page must not narrow its brands and channels either.
  const regionScope = showRegion ? f.region : "all";
  const scoped = useMemo(
    () => availability.filter((a) =>
      (regionScope === "all" || a.region === regionScope) &&
      (!activity || a.activity_type === activity)),
    [availability, regionScope, activity],
  );
  const availBrandIds = useMemo(() => new Set(scoped.map((a) => a.brand_id)), [scoped]);
  const availChannelIds = useMemo(() => new Set(scoped.map((a) => a.platform_id)), [scoped]);
  const hasAvail = availability.length > 0;

  // If the current brand/channel is no longer valid for the region, drop back to "all".
  useEffect(() => {
    if (hasAvail && f.brand !== "all" && !availBrandIds.has(f.brand)) f.setBrand("all");
  }, [hasAvail, f.brand, availBrandIds]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (hasAvail && f.channel !== "all" && !availChannelIds.has(f.channel)) f.setChannel("all");
  }, [hasAvail, f.channel, availChannelIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleBrands = hasAvail ? brands.filter((b) => availBrandIds.has(b.id)) : brands;
  // Brands are named after their Instagram accounts (Prado Official, Prado USA)
  // because awareness is where they differ; on a page like Jobs that split is
  // just "Prado" — the market picker already separates EU from US.
  const plainName = (b: { name: string }) =>
    activity === "job_ads" ? b.name.replace(/^Prado Official$/, "Prado") : b.name;
  const brandItems: Item[] = [{ value: "all", label: "Alle merken" }, ...visibleBrands.map((b) => ({ value: b.id, label: plainName(b) }))];
  // Only real media channels appear in the channel filter — GA4
  // are tools, not channels (they live under Sources).
  const visibleChannels = platforms.filter((p) => p.kind === "channel" && (!hasAvail || availChannelIds.has(p.id)));
  const channelItems: Item[] = [
    { value: "all", label: "Alle channels" },
    ...visibleChannels.map((p) => ({ value: p.id, label: p.name })),
  ];
  const currencyItems: Item[] = currencyLocked
    ? [{ value: "LOCAL", label: "Lokale valuta" }]
    : CURRENCIES.map((c) => ({ value: c, label: c }));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {showActivity && (
        <FilterSelect label="Activiteit" width="w-[150px]"
          value={f.activity} onChange={(v) => f.setActivity(v as ActivityFilter)} items={ACTIVITY_ITEMS} />
      )}
      {showRegion && (
        <FilterSelect label="Markt" width="w-[138px]"
          value={f.region} onChange={(v) => f.setRegion(v as RegionFilter)} items={REGION_ITEMS} />
      )}
      {showBrand && (
        <FilterSelect label="Merk" width="w-[150px]"
          value={f.brand} onChange={f.setBrand} items={brandItems} />
      )}
      {showChannel && (
        <FilterSelect label="Channel" width="w-[150px]"
          value={f.channel} onChange={f.setChannel} items={channelItems} />
      )}
      {showCurrency && (
        <span title={f.currency === "EUR" ? undefined : "ECB-dagkoersen; eigen valuta blijft behouden"}>
          <FilterSelect label="Rapportagevaluta" width="w-[132px]" disabled={currencyLocked}
            value={currencyLocked ? "LOCAL" : f.currency}
            onChange={(v) => v !== "LOCAL" && f.setCurrency(v as CurrencyCode)} items={currencyItems} />
        </span>
      )}
      <FilterSelect label="Periode" width="w-[126px]"
        value={f.range ? "custom" : String(f.days)}
        onChange={(v) => (v === "custom" ? f.setRange({ from: f.fromDate, to: f.toDate }) : f.setDays(Number(v)))}
        items={PERIOD_ITEMS} />
      {f.range && (
        <div className="flex items-center gap-1.5">
          {/* Up to yesterday, the last complete day, as the preset windows: a
              future day made every platform's reach request fail. */}
          <DatePicker value={f.range.from} max={lastCompleteDay} onChange={(v) => v && f.setRange({ from: v, to: f.range!.to < v ? v : f.range!.to })} className="h-8 w-[136px] text-xs" />
          <span className="text-xs text-muted-foreground">tot</span>
          <DatePicker value={f.range.to} max={lastCompleteDay} onChange={(v) => v && f.setRange({ from: f.range!.from > v ? v : f.range!.from, to: v })} className="h-8 w-[136px] text-xs" />
        </div>
      )}

      {dirty && (
        <Button variant="ghost" size="sm" onClick={f.reset} className="ml-auto h-8 gap-1.5 text-xs text-muted-foreground">
          <HugeiconsIcon icon={RotateCcwIcon} size={14} />
          Reset
        </Button>
      )}
    </div>
  );
}
